import React, { KeyboardEvent, Ref } from 'react'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import InputBase from 'components_new/atoms/InputBase'

import GenericAvatar from 'components_new/molecules/GenericAvatar'

interface ChatInputProps {
  autoFocus?: boolean
  border?: boolean
  disabled?: boolean
  inputRef?: Ref<any>
  maxRows?: number
  multiline?: boolean
  onBlur?: () => void
  onChange: (value: string) => void
  // Shown in place of send while a reply is streaming.
  onStop?: () => void
  onSubmit: () => void
  placeholder?: string
  size?: 'small' | 'medium'
  sx?: object
  value: string
}

/**
 * Rounded text input with an inline send button — the shared look for the AI
 * chat prompt, the analysis comment composer and comment-thread replies.
 * `size="small"` for the comment variants.
 */
const ChatInput = (props: ChatInputProps) => {
  const {
    autoFocus,
    border,
    disabled,
    inputRef,
    maxRows,
    multiline,
    onBlur,
    onChange,
    onStop,
    onSubmit,
    placeholder,
    size = 'medium',
    sx,
    value
  } = props

  const account = useAccountSettings()

  const isSmall = size === 'small'

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()

      if (!disabled && value.trim()) {
        onSubmit()
      }
    }
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: 'background.paper',
        borderRadius: isSmall ? 2 : 3,
        display: 'flex',
        px: isSmall ? 1.5 : 2,
        py: isSmall ? 0.5 : 1,
        width: '100%',
        ...(border ? { border: '1px solid', borderColor: 'divider' } : {}),
        [':hover']: { borderColor: 'brand.main' },
        [':focus-within']: {
          borderColor: 'brand.main',
          boxShadow: border ? 12 : 0
        },
        ...sx
      }}
    >
      <InputBase
        autoFocus={autoFocus}
        disabled={disabled}
        fullWidth={true}
        inputProps={{ autocomplete: 'off' }}
        inputRef={inputRef}
        margin="none"
        maxRows={maxRows}
        multiline={multiline}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        // size={size}
        endAdornment={
          onStop ? (
            <IconButton color="primary" onClick={onStop} size="small">
              <Icon fontSize="small" name="Stop" />
            </IconButton>
          ) : (
            <IconButton
              color="primary"
              disabled={disabled || !value.trim()}
              onClick={onSubmit}
              size="small"
            >
              <Icon fontSize="small" name="Send" />
            </IconButton>
          )
        }
        startAdornment={
          <GenericAvatar
            body={account.account?.email}
            size="xsmall"
            title={
              account.isHomepal ? 'Homepal' : account.account?.first_name ?? ''
            }
            variant={account.isHomepal ? 'homepal' : 'user'}
            sx={{ mr: 1 }}
          />
        }
        value={value}
      />
    </Box>
  )
}

export default ChatInput
